<template>
  <div :class="cardClasses">
    <div v-if="$slots.header" class="card-header">
      <slot name="header"></slot>
    </div>
    <div :class="['card-body', paddingClasses]">
      <slot></slot>
    </div>
    <div v-if="$slots.footer" class="card-footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  variant?: 'default' | 'elevated' | 'outlined' | 'glass';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hoverable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  padding: 'md',
  hoverable: false,
});

const cardClasses = computed(() => {
  const baseClasses = [
    'card',
    'rounded-xl',
    'transition-all',
    'duration-normal',
  ];

  // Variants
  const variantClasses = {
    default: [
      'bg-white',
      'border',
      'border-neutral-200',
    ],
    elevated: [
      'bg-white',
      'shadow-lg',
    ],
    outlined: [
      'bg-transparent',
      'border-2',
      'border-primary-200',
    ],
    glass: [
      'glass',
      'bg-white/10',
      'backdrop-blur-lg',
      'border',
      'border-white/20',
    ],
  };

  const classes = [
    ...baseClasses,
    ...variantClasses[props.variant],
  ];

  if (props.hoverable) {
    classes.push('hover-lift', 'cursor-pointer');
  }

  return classes.join(' ');
});

const paddingClasses = computed(() => {
  const paddings = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };
  return paddings[props.padding];
});
</script>

<style scoped>
.card {
  display: flex;
  flex-direction: column;
}

.card-header {
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--border-color-default);
}

.card-body {
  flex: 1;
}

.p-4 {
  padding: var(--spacing-4);
}

.p-6 {
  padding: var(--spacing-6);
}

.p-8 {
  padding: var(--spacing-8);
}

.card-footer {
  padding: var(--spacing-6);
  border-top: 1px solid var(--border-color-default);
  background-color: var(--color-neutral-50);
  border-bottom-left-radius: var(--border-radius-xl);
  border-bottom-right-radius: var(--border-radius-xl);
}
</style>

